import React, { useState, useEffect } from 'react';
import { TextField, Switch, FormControlLabel, Alert, Typography, Box, Paper } from '@mui/material';
import TenantSharesList from './TenantSharesList';
import { useApiClient } from '../hooks/useApiClient';
import '../styles/_buttons.scss';
import './TenantDetails.scss';

interface Tenant {
    id: string;
    ownerId: string;
    name: string;
    email: string;
    status: 'active' | 'inactive' | 'suspended';
    verified: boolean;
    shared: boolean;
    createdAt: string;
    updatedAt: string;
}

interface TenantDetailsProps {
    tenant: Tenant | null;
    loading: boolean;
    onSave: (data: { name: string; email: string }) => Promise<void>;
    onDelete: () => Promise<void>;
    editorActions?: any;
    setEditorState?: (state: any) => void;
}

const TenantDetails: React.FC<TenantDetailsProps> = ({ tenant, loading, onSave, onDelete, editorActions, setEditorState }) => {
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [formData, setFormData] = useState({ name: '', email: '' });
    const apiClient = useApiClient();

    const isFormValid = formData.name?.trim().length > 0 && 
        formData.email?.trim().length > 0 && 
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email?.trim() || '');

    useEffect(() => {
        if (tenant) {
            console.log('TenantDetails - tenant object:', tenant);
            console.log('TenantDetails - tenant.id:', tenant.id);
            setFormData({ name: tenant.name || '', email: tenant.email || '' });
        }
    }, [tenant]);

    useEffect(() => {
        if (setEditorState) {
            setEditorState({ saveEnabled: isFormValid, deleteEnabled: true });
        }
    }, [setEditorState, isFormValid]);

    useEffect(() => {
        if (onSave && editorActions) {
            editorActions.onSave = async () => {
                if (setEditorState) setEditorState({ saving: true });
                try {
                    await onSave({ name: formData.name, email: formData.email });
                } catch (err: any) {
                    setError(err.message || 'Failed to save tenant');
                } finally {
                    if (setEditorState) setEditorState({ saving: false });
                }
            };
        }
    }, [editorActions, onSave, formData, setEditorState]);

    useEffect(() => {
        if (onDelete && editorActions) {
            editorActions.onDelete = async () => {
                if (setEditorState) setEditorState({ saving: true });
                try {
                    await onDelete();
                } catch (err: any) {
                    setError(err.message || 'Failed to delete tenant');
                } finally {
                    if (setEditorState) setEditorState({ saving: false });
                }
            };
        }
    }, [editorActions, onDelete, setEditorState]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && isFormValid && editorActions?.onSave) {
            editorActions.onSave();
        }
    };

    const handleToggleStatus = async () => {
        if (!tenant) return;

        setSaving(true);
        setError(null);
        setSuccess(null);

        try {
            const newStatus = tenant.status === 'active' ? 'inactive' : 'active';
            const response = await apiClient.put(`/tenants/${tenant.id}`, {
                name: tenant.name,
                email: tenant.email,
                status: newStatus
            });

            if (response.ok) {
                setSuccess(`Tenant ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
            } else {
                const errorData = await response.json();
                setError(errorData.error || 'Failed to update tenant status');
            }
        } catch (err) {
            setError('Error updating tenant status');
        } finally {
            setSaving(false);
        }
    };

    const handleToggleVerified = async () => {
        if (!tenant) return;

        setSaving(true);
        setError(null);
        setSuccess(null);

        try {
            const response = await apiClient.post(`/tenants/${tenant.id}/verify`);

            if (response.ok) {
                setSuccess('Tenant verification status updated successfully');
            } else {
                const errorData = await response.json();
                setError(errorData.error || 'Failed to update tenant verification');
            }
        } catch (err) {
            setError('Error updating tenant verification');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div>Loading tenant details...</div>;
    }

    if (!tenant) {
        return <div>Tenant not found</div>;
    }

    return (
        <div>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mb: 2 }}>
                    {success}
                </Alert>
            )}

            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Details
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }} onKeyDown={handleKeyDown}>
                    <TextField
                        label="Name"
                        value={formData.name}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, name: e.target.value })}
                        required
                        fullWidth
                        variant="outlined"
                        error={!formData.name?.trim()}
                        helperText={!formData.name?.trim() ? 'Name is required' : ''}
                    />
                    <TextField
                        label="Email"
                        type="email"
                        value={formData.email}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, email: e.target.value })}
                        required
                        fullWidth
                        variant="outlined"
                        error={!formData.email?.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email?.trim() || '')}
                        helperText={!formData.email?.trim() ? 'Email is required' : (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email?.trim() || '') ? 'Invalid email format' : '')}
                    />

                </Box>
            </Paper>

            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Statuses
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={tenant.status === 'active'}
                                onChange={handleToggleStatus}
                                disabled={saving}
                            />
                        }
                        label="Active"
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={tenant.verified}
                                onChange={handleToggleVerified}
                                disabled={saving}
                            />
                        }
                        label="Verified"
                    />
                </Box>
            </Paper>

            {tenant && (
                <Paper sx={{ p: 3 }}>
                    <TenantSharesList tenantId={tenant.id} tenantName={tenant.name} />
                </Paper>
            )}
        </div>
    );
};

export default TenantDetails;